import axios from "axios";
import { EventType } from "./eventmodels.js";
import { MemoryCache } from "../inmemorycache.js";
import { Logger } from "../logging/logger.js";
import { Util } from "../util.js";

class NetworkControllerClass {
  constructor() {
    this.initialize = this.initialize.bind(this);
  }

  /**
   * Processes the event and calls the appropriate callback.
   * @param {RCEvent} event
   */
  processEvent(event) {
    switch (event.data.eventType) {
      case EventType.INIT:
        this.initialize(event);
        break;

      case EventType.GET:
        this.httpGet(this.axiosInstance, event, this.transformResponse);
        break;

      case EventType.POST:
        this.httpPost(this.axiosInstance, event, this.transformResponse);
        break;

      case EventType.PATCH:
        this.httpPatch(this.axiosInstance, event, this.transformResponse);
        break;

      case EventType.DELETE:
        this.httpDelete(this.axiosInstance, event, this.transformResponse);
        break;

      default:
        Logger.log("Invalid event type");
    }
  }

  initialize(event) {
    const baseURL = MemoryCache.baseURL;
    const developerToken = MemoryCache.developerToken;

    if (baseURL !== undefined && developerToken !== undefined) {
      this.axiosInstance = axios.create({
        baseURL: baseURL,
        headers: {
          "Developer-Token": `JWT ${developerToken}`,
          Accept: "*/*",
        },
      });
      event.callback.onSuccess(null);
    } else {
      event.callback.onFailure("Invalid base url or developer token");
    }
  }

  /**
   * Fires an http get request with the event params.
   * @param {AxiosInstance} axiosInstance - Axios instance
   * @param {RCEvent} event - Event with all request params.
   * @param {Function} transformResponse - Transformation function.
   */
  httpGet(axiosInstance, event, transformResponse) {
    if (!event.data) {
      if (event.callback) {
        event.callback.onFailure(Util.getErrorJson("No event data found"));
      } else {
        Logger.log("No event data found");
      }
      return;
    }

    axiosInstance
      .get(event.data.path, {
        params: event.data.queryData,
        headers: {
          ...(MemoryCache.authorizationToken && {
            Authorization: `JWT ${MemoryCache.authorizationToken}`,
          }),
        },
        transformResponse: transformResponse,
      })
      .then((response) => {
        event.callback.onSuccess(response.data);
      })
      .catch((error) => {
        event.callback.onFailure(error);
      });
  }

  /**
   * Fires an http post request with the event params.
   * @param {AxiosInstance} axiosInstance - Axios instance
   * @param {RCEvent} event - Event with all request params.
   * @param {Function} transformResponse - Transformation function.
   */
  httpPost(axiosInstance, event, transformResponse) {
    if (!event.data) {
      if (event.callback) {
        event.callback.onFailure(Util.getErrorJson("No event data found"));
      } else {
        Logger.log("No event data found");
      }
      return;
    }

    axiosInstance
      .post(event.data.path, event.data.formData, {
        params: event.data.queryData,
        headers: {
          ...(MemoryCache.authorizationToken && {
            Authorization: `JWT ${MemoryCache.authorizationToken}`,
          }),
          "Content-Type":
            event.data && event.data.isMultipart
              ? "multipart/form-data"
              : "application/json",
        },
        transformResponse: transformResponse,
      })
      .then((response) => {
        event.callback.onSuccess(response.data);
      })
      .catch((error) => {
        event.callback.onFailure(error);
      });
  }

  /**
   * Fires an http patch request with the event params.
   * @param {AxiosInstance} axiosInstance - Axios instance
   * @param {RCEvent} event - Event with all request params.
   * @param {Function} transformResponse - Transformation function.
   */
  httpPatch(axiosInstance, event, transformResponse) {
    if (!event.data) {
      if (event.callback) {
        event.callback.onFailure(Util.getErrorJson("No event data found"));
      } else {
        Logger.log("No event data found");
      }
      return;
    }

    axiosInstance
      .patch(event.data.path, event.data.formData, {
        params: event.data.queryData,
        headers: {
          ...(MemoryCache.authorizationToken && {
            Authorization: `JWT ${MemoryCache.authorizationToken}`,
          }),
          "Content-Type": "application/json",
        },
        transformResponse: transformResponse,
      })
      .then((response) => {
        event.callback.onSuccess(response.data);
      })
      .catch((error) => {
        event.callback.onFailure(error);
      });
  }

  /**
   * Fires an http patch request with the event params.
   * @param {AxiosInstance} axiosInstance - Axios instance
   * @param {RCEvent} event - Event with all request params.
   * @param {Function} transformResponse - Transformation function.
   */
  httpDelete(axiosInstance, event, transformResponse) {
    if (!event.data) {
      if (event.callback) {
        event.callback.onFailure(Util.getErrorJson("No event data found"));
      } else {
        Logger.log("No event data found");
      }
      return;
    }

    axiosInstance
      .delete(event.data.path, {
        headers: {
          ...(MemoryCache.authorizationToken && {
            Authorization: `JWT ${MemoryCache.authorizationToken}`,
          }),
          "Content-Type": "application/json",
        },
        ...(event.data.formData && {
          data: event.data.formData,
        }),
        transformResponse: transformResponse,
      })
      .then((response) => {
        event.callback.onSuccess(response.data);
      })
      .catch((error) => {
        event.callback.onFailure(error);
      });
  }

  transformResponse(data) {
    let response = {};
    try {
      response = JSON.parse(data);
    } catch (err) {
      const error = { error: err.message };
      response = JSON.stringify(error);
    }

    return response;
  }
}

export const NetworkController = new NetworkControllerClass();
